import React, { useState, useEffect, useCallback } from 'react'
import {
  Show,
  SimpleShowLayout,
  TextField,
  useDataProvider,
  useGetOne,
  Button,
  useRedirect,
  useNotify,
  useGetIdentity,
} from 'react-admin'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { useParams } from 'react-router-dom'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField as MuiTextField,
  Typography,
  Chip,
  Button as MuiButton,
  Alert,
  Divider,
  Grid,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SendIcon from '@mui/icons-material/Send'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { sendQuestionnaire } from './SendQuestionnaireService'
import userPermissions from '../Users/userPermissions'
import QuestionnaireReviewForm from './QuestionnaireReviewForm'

import { VendorQuestionnaireStatuses } from './types'

// Define TypeScript interfaces for questionnaires
interface Questionnaire {
  id: string
  name: string
  description: string
}

// Define TypeScript interface for organization risk levels
interface OrganisationRiskLevel {
  id: number
  organisation_id: number
  name: string
  color: string
  low_range: number
  high_range: number
  deleted: boolean
}

interface VendorQuestionnaire {
  id: number
  questionnaire_id: string
  status: number
  vendor_email_address: string
  agent_user_id: number
  completed_questionnaire?: Record<string, string>
  score?: number
  percentage_score?: number
  vendor_id: number
  completed_at?: string
  vendor?: {
    name: string
    [key: string]: any
  }
  questionnaire?: {
    id: string
    name: string
    description: string
    type?: string
    [key: string]: any
  }
}

interface VendorRiskAssessment {
  vendor_id: number
  risk_level: number
  compliance_score: number
  assessment_status: number
  last_assessment?: string
  risk_assessor_id?: string
  risk_level_name?: string
  risk_level_color?: string
}

// Helper function to format expiry minutes into a human-readable format
const formatExpiryTime = (minutes?: number): string => {
  if (minutes === undefined) return 'Not set'

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  if (hours === 0) {
    return `${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
  }
  if (remainingMinutes === 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`
  }
  return `${hours} hour${hours !== 1 ? 's' : ''} ${remainingMinutes} minute${
    remainingMinutes !== 1 ? 's' : ''
  }`
}

// Status mapping for questionnaire status
const getStatusText = (status: number): string => {
  switch (status) {
    case VendorQuestionnaireStatuses.Pending:
      return 'Awaiting Review'
    case VendorQuestionnaireStatuses.InProgress:
      return 'In Progress'
    case VendorQuestionnaireStatuses.VendorSubmitted:
      return 'Submitted'
    case VendorQuestionnaireStatuses.Completed:
      return 'Completed'
    case VendorQuestionnaireStatuses.Expired:
      return 'Expired'
    case VendorQuestionnaireStatuses.Cancelled:
      return 'Cancelled'
    case VendorQuestionnaireStatuses.Rejected:
      return 'Rejected'
    case VendorQuestionnaireStatuses.Resubmitted:
      return 'Resubmitted'
    case VendorQuestionnaireStatuses.ClarificationRequested:
      return 'Clarification Requested'
    default:
      return 'Unknown'
  }
}

const RiskManagementShow = () => {
  const { id } = useParams<{ id: string }>()
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const redirect = useRedirect()
  const { identity } = useGetIdentity()
  const { data: vendor, isLoading: isLoadingVendor } = useGetOne('vendors', {
    id,
  })
  const [questionnaires, setQuestionnaires] = useState<VendorQuestionnaire[]>(
    []
  )
  const [awaitingReviewQuestionnaires, setAwaitingReviewQuestionnaires] =
    useState<VendorQuestionnaire[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingReviewQuestionnaires, setLoadingReviewQuestionnaires] =
    useState(true)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<VendorQuestionnaire | null>(null)
  const [questionnaireDetails, setQuestionnaireDetails] = useState<any>(null)
  const [riskAssessment, setRiskAssessment] =
    useState<VendorRiskAssessment | null>(null)
  const [updatingRiskAssessment, setUpdatingRiskAssessment] = useState(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  const [viewSource, setViewSource] = useState<'vendor' | 'review' | null>(null)
  const [overrideScore, setOverrideScore] = useState<number | null>(null)
  const [organisationRiskLevels, setOrganisationRiskLevels] = useState<
    OrganisationRiskLevel[]
  >([])
  const [loadingRiskLevels, setLoadingRiskLevels] = useState(false)

  // Check if all questionnaires are completed
  const areAllQuestionnairesCompleted = useCallback(() => {
    if (questionnaires.length === 0) return false
    return questionnaires.every((q) => q.status === 3 || q.status === 4) // Status 3 = Completed, Status 4 = Assessed
  }, [questionnaires])

  // Check if any questionnaire is in a status other than "Completed" or "Assessed"
  const hasNonCompletedQuestionnaires = useCallback(() => {
    if (questionnaires.length === 0) return false
    return questionnaires.some((q) => q.status !== 3 && q.status !== 4) // Status 3 = Completed, Status 4 = Assessed
  }, [questionnaires])

  // Calculate compliance score (100 - average percentage from completed questionnaires)
  const calculateComplianceScore = useCallback(() => {
    const completedQuestionnaires = questionnaires.filter(
      (q) => q.status === 3 || q.status === 4
    )
    if (completedQuestionnaires.length === 0) return 0

    const totalPercentage = completedQuestionnaires.reduce((sum, q) => {
      return sum + (q.percentage_score || 0)
    }, 0)

    const averagePercentage = totalPercentage / completedQuestionnaires.length
    return Math.round(100 - averagePercentage)
  }, [questionnaires])

  // Determine risk level based on compliance score
  const determineRiskLevel = useCallback(
    (complianceScore: number) => {
      // If we have organization risk levels, use them
      if (organisationRiskLevels.length > 0) {
        // Find the risk level that matches the compliance score
        const matchingRiskLevel = organisationRiskLevels.find(
          (riskLevel) =>
            complianceScore >= riskLevel.low_range &&
            complianceScore <= riskLevel.high_range
        )

        // If we found a matching risk level, return its ID
        if (matchingRiskLevel) {
          return matchingRiskLevel.id
        }
      }

      // If no organization risk levels are available, return the first risk level ID if we can fetch it later
      // or a default value that will be handled by getRiskLevelDetails
      return -1 // Unknown risk level
    },
    [organisationRiskLevels]
  )

  // Get risk level details (name, color) based on risk level ID
  const getRiskLevelDetails = (riskLevelId: number) => {
    // If we have organization risk levels, try to find the matching one
    if (organisationRiskLevels.length > 0) {
      const riskLevel = organisationRiskLevels.find(
        (rl) => rl.id === riskLevelId
      )
      if (riskLevel) {
        return {
          name: riskLevel.name,
          color: riskLevel.color,
        }
      }
    }

    // Fallback to a generic "Unknown" risk level if no matching organization risk level is found
    return { name: 'Unknown', color: '#9e9e9e' }
  }

  // Update the risk assessment state based on the current questionnaires
  const updateRiskAssessmentState = useCallback(() => {
    // If there are no questionnaires, set assessment status to "Pending" (0)
    if (questionnaires.length === 0) {
      setRiskAssessment((prevState) => ({
        ...prevState,
        assessment_status: 0, // Pending
        vendor_id: Number(id),
        risk_level: prevState?.risk_level ?? -1, // Default to unknown risk level (-1) if not set
        compliance_score: prevState?.compliance_score ?? 0, // Default to 0 if not set
        risk_assessor_id: prevState?.risk_assessor_id ?? String(identity?.id), // Preserve existing risk_assessor_id or set to current user
      }))
      return
    }

    // If any questionnaire is not completed, set assessment status to "Pending" (3)
    // If all questionnaires are completed, keep "Completed" (4) if already completed, otherwise set to "Completed" (4)
    const newAssessmentStatus = hasNonCompletedQuestionnaires()
      ? 3 // "Pending" (3) if any questionnaire is not completed
      : areAllQuestionnairesCompleted()
      ? 4 // "Completed" (4) if all questionnaires are completed
      : 3 // "Pending" (3) if no questionnaires are completed yet

    // Calculate compliance score and risk level if all questionnaires are completed
    const complianceScore = calculateComplianceScore()
    const riskLevel = determineRiskLevel(complianceScore)

    // Update the risk assessment state
    setRiskAssessment((prevState) => ({
      ...prevState,
      vendor_id: Number(id),
      assessment_status: newAssessmentStatus,
      compliance_score: complianceScore,
      risk_level: riskLevel,
      risk_assessor_id: prevState?.risk_assessor_id ?? String(identity?.id), // Preserve existing risk_assessor_id or set to current user
    }))
  }, [
    questionnaires,
    id,
    identity,
    hasNonCompletedQuestionnaires,
    areAllQuestionnairesCompleted,
    calculateComplianceScore,
    determineRiskLevel,
  ])

  // Update the risk assessment on the server
  const updateRiskAssessment = async () => {
    if (!id) return

    try {
      setUpdatingRiskAssessment(true)

      const complianceScore = calculateComplianceScore()
      const riskLevel = determineRiskLevel(complianceScore)

      const riskAssessmentData = {
        vendor_id: Number(id),
        risk_level: riskLevel,
        compliance_score: complianceScore,
        assessment_status: 3, // Completed
        last_assessment: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
        risk_assessor_id: String(identity?.id), // Set the current user as the risk assessor
      }

      // Create or update the risk assessment
      await dataProvider.custom(`risk-assessment/${id}`, {
        method: 'PUT',
        body: JSON.stringify(riskAssessmentData),
      })

      // Update the local state
      setRiskAssessment(riskAssessmentData)
      setShowSuccessAlert(true)

      // Hide the success alert after 5 seconds
      setTimeout(() => {
        setShowSuccessAlert(false)
      }, 5000)

      notify('Risk assessment updated successfully', { type: 'success' })
    } catch (error) {
      console.error('Error updating risk assessment:', error)
      notify('Error updating risk assessment', { type: 'error' })
    } finally {
      setUpdatingRiskAssessment(false)
    }
  }

  const loadQuestionnaires = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)

      // Fetch vendor questionnaires from the API
      try {
        const { data } = await dataProvider.custom(
          `questionnaires/vendors/${id}`,
          { method: 'GET' }
        )
        setQuestionnaires(data || [])
      } catch (e) {
        console.error('Error fetching questionnaires from API:', e)

        // Fallback to localStorage for backward compatibility
        const storageKey = `vendor_questionnaires_${id}`
        const storedQuestionnaires = JSON.parse(
          localStorage.getItem(storageKey) || '[]'
        )

        if (storedQuestionnaires.length > 0) {
          setQuestionnaires(storedQuestionnaires)

          // Migrate localStorage data to API
          const migrationPromises = storedQuestionnaires.map((questionnaire) =>
            dataProvider
              .custom(`questionnaires/vendors/${id}`, {
                method: 'POST',
                body: JSON.stringify(questionnaire),
              })
              .catch((migrationError) => {
                console.error(
                  'Error migrating questionnaire to API:',
                  migrationError
                )
              })
          )

          await Promise.all(migrationPromises)

          // Clear localStorage after migration
          localStorage.removeItem(storageKey)
        } else {
          setQuestionnaires([])
        }
      }

      // Load the risk assessment data if available
      if (vendor && vendor.risk_assessment) {
        setRiskAssessment(vendor.risk_assessment)
      }
    } catch (error) {
      console.error('Error loading questionnaires:', error)
      setQuestionnaires([])
    } finally {
      setLoading(false)

      // Update the risk assessment state based on the loaded questionnaires
      // This is done after loading is complete to ensure the questionnaires state is updated
      setTimeout(() => {
        updateRiskAssessmentState()
      }, 0)
    }
  }, [dataProvider, id, vendor, updateRiskAssessmentState])

  // Load questionnaires that are awaiting review by the current user
  const loadAwaitingReviewQuestionnaires = useCallback(async () => {
    if (!identity?.id) return

    try {
      setLoadingReviewQuestionnaires(true)

      // Fetch questionnaires that are in the "Reviewing" status and assigned to the current user
      // This endpoint will need to be implemented in the backend
      const { data } = await dataProvider.custom(
        `questionnaires/awaiting-review`,
        {
          method: 'GET',
          params: {
            agent_user_id: identity.id,
            status: 5, // Reviewing status
          },
        }
      )

      setAwaitingReviewQuestionnaires(data || [])
    } catch (error) {
      console.error('Error loading questionnaires awaiting review:', error)
      setAwaitingReviewQuestionnaires([])
    } finally {
      setLoadingReviewQuestionnaires(false)
    }
  }, [dataProvider, identity])

  // Load users with supervisor flag
  const loadUsers = useCallback(async () => {
    try {
      // Get all users
      const { data: allUsers } = await dataProvider.getList('users', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      })

      // Filter users with either:
      // 1. SUPER_ADMIN permission (1024) - for super admins
      // 2. EDIT_TRANSACTIONS permission (128) - for users who can edit vendors
      const relevantUsers = allUsers.filter(
        (user) =>
          (user.permission_flags & userPermissions.SUPER_ADMIN) ===
            userPermissions.SUPER_ADMIN ||
          (user.permission_flags & userPermissions.EDIT_TRANSACTIONS) ===
            userPermissions.EDIT_TRANSACTIONS
      )

      setUsers(relevantUsers)
    } catch (error) {
      console.error('Error loading users:', error)
      notify('Error loading users', { type: 'error' })
    }
  }, [dataProvider, notify])

  // Handle deleting a questionnaire
  const handleDeleteQuestionnaire = async (vendorQuestionnaireId: number) => {
    if (!id) return

    try {
      // Delete the questionnaire using the API
      await dataProvider.custom(
        `questionnaires/vendors/${id}/${vendorQuestionnaireId}`,
        { method: 'DELETE' }
      )

      // Update state by removing the deleted questionnaire
      setQuestionnaires((prevQuestionnaires) =>
        prevQuestionnaires.filter((q) => q.id !== vendorQuestionnaireId)
      )

      // Update the risk assessment state based on the updated questionnaires list
      setTimeout(() => {
        updateRiskAssessmentState()
      }, 0)

      notify('Questionnaire deleted successfully', { type: 'success' })
    } catch (error) {
      console.error('Error deleting questionnaire:', error)
      notify('Error deleting questionnaire', { type: 'error' })
    }
  }

  // Handle sending a questionnaire
  const handleSendQuestionnaire = async (
    questionnaireId: string,
    vendorQuestionnaireId: number
  ) => {
    if (!id) return

    try {
      // Use the shared sendQuestionnaire function
      const result = await sendQuestionnaire({
        dataProvider,
        questionnaireId,
        vendorIds: [Number(id)], // Convert id to number and wrap in array
        notify, // Pass the notify function for error handling
      })

      if (result.successCount > 0) {
        // Update state by updating the status of the questionnaire
        setQuestionnaires((prevQuestionnaires) =>
          prevQuestionnaires.map((q) => {
            if (q.id === vendorQuestionnaireId) {
              return { ...q, status: 1 }
            }
            return q
          })
        )

        // Update the risk assessment state based on the updated questionnaires list
        setTimeout(() => {
          updateRiskAssessmentState()
        }, 0)

        notify('Questionnaire sent successfully', { type: 'success' })
      } else if (result.errorCount === 0) {
        // If no success but no error was thrown, show a generic error
        notify('Error sending questionnaire', { type: 'error' })
      }
      // Errors are handled by the sendQuestionnaire function via the notify callback
    } catch (error) {
      console.error('Error sending questionnaire:', error)

      // Check for specific email verification errors
      if (error && typeof error === 'object') {
        // Check if error has data property (from dataProvider's custom error)
        if ('data' in error && error.data && typeof error.data === 'object') {
          // Check for email not verified error
          if (
            'error' in error.data &&
            error.data.error === 'email_not_verified'
          ) {
            // Show a confirmation dialog
            if (
              window.confirm(
                `Your email address needs to be verified before sending. A verification email has been sent to your email address. Please verify your email and try again.`
              )
            ) {
              // User confirmed, they will verify their email
              notify('Please check your email for verification instructions', {
                type: 'info',
              })
            }
            return
          }

          // Check for domain not verified error
          if (
            'error' in error.data &&
            error.data.error === 'domain_not_verified'
          ) {
            notify(
              'Your email domain is not verified in AWS SES. Please contact your administrator.',
              { type: 'error' }
            )
            return
          }

          // If there's a specific error message in the response, use it
          if ('message' in error.data && error.data.message) {
            notify(String(error.data.message), { type: 'error' })
            return
          }
        }

        // Also check for body property (for backward compatibility)
        if ('body' in error && error.body && typeof error.body === 'object') {
          // Check for email not verified error
          if (
            'error' in error.body &&
            error.body.error === 'email_not_verified'
          ) {
            // Show a confirmation dialog
            if (
              window.confirm(
                `Your email address needs to be verified before sending. A verification email has been sent to your email address. Please verify your email and try again.`
              )
            ) {
              // User confirmed, they will verify their email
              notify('Please check your email for verification instructions', {
                type: 'info',
              })
            }
            return
          }

          // Check for domain not verified error
          if (
            'error' in error.body &&
            error.body.error === 'domain_not_verified'
          ) {
            notify(
              'Your email domain is not verified in AWS SES. Please contact your administrator.',
              { type: 'error' }
            )
            return
          }

          // If there's a specific error message in the response, use it
          if ('message' in error.body && error.body.message) {
            notify(String(error.body.message), { type: 'error' })
            return
          }
        }

        // If error has a message property, use it
        if (
          'message' in error &&
          typeof error.message === 'string' &&
          error.message.includes('Domain') &&
          error.message.includes('not verified')
        ) {
          notify(
            'Your email domain is not verified in AWS SES. Please contact your administrator.',
            { type: 'error' }
          )
          return
        }
      }

      // Default error message
      notify('Error sending questionnaire', { type: 'error' })
    }
  }

  // Handle viewing a completed questionnaire
  const handleViewQuestionnaire = async (
    vendorQuestionnaireId: number,
    source: 'vendor' | 'review' = 'vendor'
  ) => {
    if (!id) return

    try {
      // Find the questionnaire in the current state
      const questionnaireToView =
        source === 'vendor'
          ? questionnaires.find((q) => q.id === vendorQuestionnaireId)
          : awaitingReviewQuestionnaires.find(
              (q) => q.id === vendorQuestionnaireId
            )

      if (!questionnaireToView) {
        notify('Questionnaire not found', { type: 'error' })
        return
      }

      // Set the selected questionnaire and view source
      setSelectedQuestionnaire(questionnaireToView)
      setViewSource(source)

      // Fetch the questionnaire template to get the structure
      try {
        const { data: questionnaireTemplate } = await dataProvider.custom(
          `questionnaires/${questionnaireToView.questionnaire_id}/organisations/current`,
          { method: 'GET' }
        )

        // Set the questionnaire details with both the template and the completed data
        setQuestionnaireDetails({
          template: questionnaireTemplate,
          completed: questionnaireToView.completed_questionnaire,
        })

        // Open the dialog
        setViewDialogOpen(true)
      } catch (error) {
        console.error('Error fetching questionnaire template:', error)
        notify('Error fetching questionnaire details', { type: 'error' })
      }
    } catch (error) {
      console.error('Error viewing questionnaire:', error)
      notify('Error viewing questionnaire', { type: 'error' })
    }
  }

  // Load organization risk levels
  const loadOrganisationRiskLevels = useCallback(
    async (organisationId: number) => {
      try {
        setLoadingRiskLevels(true)

        // Fetch organization risk levels from the API
        const { data } = await dataProvider.custom(`organisation/risk-levels`, {
          method: 'GET',
        })

        // If we got data back, use it; otherwise use an empty array
        setOrganisationRiskLevels(data || [])
      } catch (error) {
        console.error('Error loading organization risk levels:', error)
        notify('Error loading risk levels', { type: 'error' })
        // If there's an error, use an empty array
        setOrganisationRiskLevels([])
      } finally {
        setLoadingRiskLevels(false)
      }
    },
    [dataProvider, notify]
  )

  useEffect(() => {
    loadQuestionnaires()
    loadUsers()
  }, [dataProvider, id, loadQuestionnaires, loadUsers])

  // Load organization risk levels when vendor data is loaded
  useEffect(() => {
    if (vendor && vendor.organisation?.id) {
      loadOrganisationRiskLevels(vendor.organisation.id)
    }
  }, [vendor, loadOrganisationRiskLevels])

  // Load questionnaires awaiting review when identity changes
  useEffect(() => {
    if (identity?.id) {
      loadAwaitingReviewQuestionnaires()
    }
  }, [dataProvider, identity, loadAwaitingReviewQuestionnaires])

  if (isLoadingVendor || loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  // Function to close the view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false)
    setSelectedQuestionnaire(null)
    setQuestionnaireDetails(null)
    setViewSource(null)
    setOverrideScore(null)
  }

  // Handle accepting a questionnaire (change status to Completed)
  const handleAcceptQuestionnaire = async () => {
    if (!selectedQuestionnaire) return

    try {
      // Update the questionnaire status using the API
      const updatedData: {
        status: number
        score?: number
        percentage_score?: number
      } = {
        status: 3, // Set status to "Completed"
      }

      // If the score has been overridden, include it in the update
      if (overrideScore !== null) {
        updatedData.score = overrideScore

        // Calculate percentage score if min_risk_score and max_risk_score are available
        if (questionnaireDetails?.template) {
          const minScore = questionnaireDetails.template.min_risk_score || 0
          const maxScore = questionnaireDetails.template.max_risk_score || 100

          if (maxScore > minScore) {
            const percentageScore =
              ((overrideScore - minScore) / (maxScore - minScore)) * 100
            // Ensure percentage is within 0-100 range
            updatedData.percentage_score = Math.max(
              0,
              Math.min(100, percentageScore)
            )
          }
        }
      }

      await dataProvider.custom(
        `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(updatedData),
        }
      )

      // Update state by updating the status of the questionnaire in awaitingReviewQuestionnaires
      setAwaitingReviewQuestionnaires((prevQuestionnaires) =>
        prevQuestionnaires.filter((q) => q.id !== selectedQuestionnaire.id)
      )

      // Close the dialog
      handleCloseViewDialog()

      // Refresh the questionnaires list
      loadQuestionnaires()
      loadAwaitingReviewQuestionnaires()

      notify('Questionnaire accepted successfully', { type: 'success' })
    } catch (error) {
      console.error('Error accepting questionnaire:', error)
      notify('Error accepting questionnaire', { type: 'error' })
    }
  }

  // Handle rejecting a questionnaire (change status to Rejected)
  const handleRejectQuestionnaire = async () => {
    if (!selectedQuestionnaire) return

    try {
      // Update the questionnaire status using the API
      const updatedData = {
        status: 6, // Set status to "Rejected"
      }

      await dataProvider.custom(
        `questionnaires/vendors/${selectedQuestionnaire.vendor_id}/${selectedQuestionnaire.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(updatedData),
        }
      )

      // Update state by updating the status of the questionnaire in awaitingReviewQuestionnaires
      setAwaitingReviewQuestionnaires((prevQuestionnaires) =>
        prevQuestionnaires.filter((q) => q.id !== selectedQuestionnaire.id)
      )

      // Close the dialog
      handleCloseViewDialog()

      // Refresh the questionnaires list
      loadQuestionnaires()
      loadAwaitingReviewQuestionnaires()

      notify('Questionnaire rejected successfully', { type: 'success' })
    } catch (error) {
      console.error('Error rejecting questionnaire:', error)
      notify('Error rejecting questionnaire', { type: 'error' })
    }
  }

  return (
    <Show resource="vendors">
      <SimpleShowLayout>
        {/* View Questionnaire Dialog */}
        <QuestionnaireReviewForm
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          selectedQuestionnaire={selectedQuestionnaire}
          viewSource={viewSource}
          onAccept={() => {
            setAwaitingReviewQuestionnaires((prevQuestionnaires) =>
              prevQuestionnaires.filter(
                (q) => q.id !== selectedQuestionnaire?.id
              )
            )
            loadQuestionnaires()
            loadAwaitingReviewQuestionnaires()
          }}
          onReject={() => {
            setAwaitingReviewQuestionnaires((prevQuestionnaires) =>
              prevQuestionnaires.filter(
                (q) => q.id !== selectedQuestionnaire?.id
              )
            )
            loadQuestionnaires()
            loadAwaitingReviewQuestionnaires()
          }}
          onRefresh={() => {
            loadQuestionnaires()
            loadAwaitingReviewQuestionnaires()
          }}
        />

        {/* Vendor Questionnaires Panel */}
        <Box mt={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <h1>Questionnaires for {vendor?.name || 'Vendor'}</h1>
          </Box>

          {questionnaires.length === 0 ? (
            <Box
              padding={2}
              bgcolor="#f5f5f5"
              borderRadius={1}
              textAlign="center"
            >
              No questionnaires found for this vendor.
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Questionnaire Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Risk Score Range</TableCell>
                    <TableCell>Expiry Time</TableCell>
                    <TableCell>Vendor Email</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {questionnaires.map((questionnaire) => (
                    <TableRow key={questionnaire.questionnaire_id}>
                      <TableCell>
                        {questionnaire.questionnaire?.name ||
                          questionnaire.questionnaire_id}
                      </TableCell>
                      <TableCell>
                        {(
                          questionnaire.questionnaire?.type || 'N/A'
                        ).toUpperCase()}
                      </TableCell>
                      <TableCell>
                        {getStatusText(questionnaire.status)}
                      </TableCell>
                      <TableCell>
                        {questionnaire.questionnaire?.min_risk_score !==
                          undefined &&
                        questionnaire.questionnaire?.max_risk_score !==
                          undefined
                          ? `${questionnaire.questionnaire.min_risk_score} - ${questionnaire.questionnaire.max_risk_score}`
                          : 'Not calculated'}
                      </TableCell>
                      <TableCell>
                        {formatExpiryTime(
                          questionnaire.questionnaire?.expiry_minutes
                        )}
                      </TableCell>
                      <TableCell>
                        {questionnaire.vendor_email_address}
                      </TableCell>
                      <TableCell>
                        {users.find(
                          (user) => user.id === questionnaire.agent_user_id
                        )?.name || questionnaire.agent_user_id}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          {questionnaire.status ===
                            VendorQuestionnaireStatuses.Pending && (
                            <Button
                              label="Send"
                              onClick={() =>
                                handleSendQuestionnaire(
                                  questionnaire.questionnaire_id,
                                  questionnaire.id
                                )
                              }
                              title="Send Questionnaire"
                            >
                              <SendIcon />
                            </Button>
                          )}
                          {(questionnaire.status ===
                            VendorQuestionnaireStatuses.VendorSubmitted ||
                            questionnaire.status ===
                              VendorQuestionnaireStatuses.Completed ||
                            questionnaire.status ===
                              VendorQuestionnaireStatuses.Cancelled ||
                            questionnaire.status ===
                              VendorQuestionnaireStatuses.Rejected) && (
                            <Button
                              label="View"
                              onClick={() =>
                                handleViewQuestionnaire(
                                  questionnaire.id,
                                  'vendor'
                                )
                              }
                              title="View Completed Questionnaire"
                            >
                              <VisibilityIcon />
                            </Button>
                          )}
                          <Button
                            label="Delete"
                            onClick={() =>
                              handleDeleteQuestionnaire(questionnaire.id)
                            }
                            title="Delete Questionnaire"
                            color="secondary"
                          >
                            <DeleteIcon />
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </SimpleShowLayout>
    </Show>
  )
}

export default RiskManagementShow
